import { CloseButton } from '@orn/shared-web/components/Button';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { useInert } from '@orn/shared-web/hooks/useInert';
import { useResizeObserver } from '@orn/shared-web/hooks/useResizeObserver';
import { SparkleIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import isPinnedToBottom from '@orn/shared-web/utils/dom/scrollPin';
import * as React from 'react';

import {
  AlertCardHeader,
  AssistantInput,
  AssistantMessage,
  AssistantWelcomeHeader,
  ConnectionIndicator,
  ThinkingIndicator,
  ToolCallLine,
  WorkItemMoveLine,
} from '../../components/assistant';
import { alertIdByMessageId } from '../../lib/assistant/alertRecap';
import {
  buildTranscriptDividers,
  clusterTranscriptRows,
  computeGroupedMessageIds,
  computeSpentSuggestionIds,
  isRenderableMessage,
} from '../../lib/assistant/messageDisplay';
import { welcomeTailSpacerHeight } from '../../lib/assistant/welcomeTailSpacer';
import { workItemMoveLine } from '../../lib/assistant/workItemMoveLine';
import { SIDE_PANEL_TRANSITION_DURATION_STYLE } from '../../utils/sidePanelMotion';

import AssistantPaneEmptyState from './AssistantPaneEmptyState';
import { useAlertsOverlayFrame } from './useAlertsOverlayFrame';

import type { IAlertsCardMotion } from './useComposerAlertsCenter';
import type { TranscriptEntities } from './useTranscriptEntities';
import type { ConnectionStatus, IConversationMessage } from '../../hooks/useAssistantWebSocket';
import type { TranscriptDivider } from '../../lib/assistant/messageDisplay';
import type { AlertRowFields } from '../../utils/alert/alertRow';
import type { AlertsCenterPresentation } from '../../utils/alert/alertsCenterCard';
import type { EntityTarget } from '../../utils/entityDestination';
import type { IObservationDocument } from '@orn/global/assistant/snapshotMessage.js';

/** What the assistant surface calls itself, in the app's sentence case. */
const ASSISTANT_TITLE = 'Assistant';

// Safety cap on how long scroll events stay suppressed during a smooth
// send-follow. The suppression normally lifts the instant the glide reaches the
// bottom; this only matters if the animation is interrupted (e.g. the user
// grabs the scrollbar) and never emits an at-bottom event — without the cap,
// pin tracking would be stranded.
const SMOOTH_FOLLOW_SETTLE_TIMEOUT_MS = 1000;

/**
 * The input region's height, written onto the pane root so the Notification
 * Center card can anchor its bottom edge above an input that grows with its
 * text and attachments. Measured because the composer's height is the one
 * thing under the card that is not deterministic.
 */
const ASSISTANT_COMPOSER_HEIGHT_VAR = '--assistant-composer-h';

/** The gap between the card and the input (Figma "Assistant Composer", `gap-2`). */
const COMPOSER_CARD_GAP_PX = 8;
/** `COMPOSER_CARD_GAP_PX` as a custom property, so the overlay's anchor can add it in CSS. */
const ASSISTANT_COMPOSER_GAP_VAR = '--assistant-composer-gap';

interface IAssistantPaneViewProps {
  messages: IConversationMessage[];
  isProcessing: boolean;
  /** Forwarded to {@link AssistantInput}: true while the live turn is abortable → button is a stop button. */
  isAbortable: boolean;
  isRehydrating: boolean;
  /**
   * True while the turn is suspended waiting on a browser-side client tool (the
   * user is completing the Plaid flow). Suppresses the "Thinking…" indicator —
   * the assistant isn't thinking, it's waiting on the user, and the tool's gray
   * activity line already says what's happening. The turn stays abortable.
   * Defaults to `false` so Storybook fixtures need not set it.
   */
  isAwaitingClientTool?: boolean;
  connectionStatus: ConnectionStatus;
  error: string | null;
  /**
   * Forwarded to {@link AssistantInput}. `attachments` is populated by the
   * composer when the user attached documents to the turn; the hook derives
   * the wire `attachmentDocumentIds` from `attachments.map(a => a.id)` and
   * reuses the same array for the locally-echoed bubble.
   */
  onSend: (content: string, attachments?: IObservationDocument[]) => void;
  /** Forwarded to {@link AssistantInput}: interrupt the in-flight turn. */
  onStop: () => void;
  /**
   * Re-claim the assistant in this tab after it was superseded by another tab
   * (`connectionStatus === 'superseded'`). Wired to the banner's "Reconnect"
   * button.
   */
  onReclaim: () => void;
  /**
   * Renders a close button in the header when provided — the `xl+` side panel
   * passes it to collapse the column; the full-page `/assistant` route below
   * `xl` omits it, since there is no panel to close there.
   */
  onClose?: () => void;
  /**
   * The Notification Center card, in whichever presentation the pane holds.
   * Rendered as an overlay anchored above the input — or covering the whole
   * pane when `isCoveredByAlerts` — while `alertsReservedHeightPx` holds its
   * space in the composer. A slot rather than a mounted component so this view
   * keeps no data dependencies and its stories need no Apollo client.
   */
  alertsSlot?: React.ReactNode;
  /** The presentation `alertsSlot` is in, or moving to; it decides where the overlay is anchored. */
  alertsPresentation?: AlertsCenterPresentation;
  /**
   * The presentation change in flight, or none at rest. While set, the overlay
   * transitions its height and anchor between the two presentations' pixel
   * endpoints and hands `transitionend` back through it.
   */
  alertsMotion?: IAlertsCardMotion;
  /**
   * The height the composer reserves under `alertsSlot`, so the input sits
   * below the card rather than under it. Zero (the default) reserves nothing;
   * a change glides at the panel's duration so the input never jumps.
   */
  alertsReservedHeightPx?: number;
  /**
   * A notice for the input region — a failed reconnect's error line — rendered
   * above the input, below the card's anchor, so the card never covers it.
   */
  composerNoticeSlot?: React.ReactNode;
  /**
   * True while the full Notification Center covers the pane, from the first
   * frame of its expand to the end of its collapse. The header, the
   * transcript, the superseded bar, and the input go inert so neither focus nor
   * a screen reader lands under the card; nothing unmounts, so the WebSocket
   * and the composer draft survive.
   */
  isCoveredByAlerts?: boolean;
  /**
   * The alerts this transcript's cards name, keyed by id. Resolved by the pane
   * and handed down for the same reason `alertsSlot` is a slot: this view
   * fetches nothing. A card whose id is absent renders its unavailable state,
   * which is also what a still-loading lookup shows — a card is a header, so
   * there is no half-rendered state worth a skeleton.
   */
  alertCards?: Record<string, AlertRowFields>;
  /**
   * Alerts the user has already seen, or that have moved on — read, waiting, or
   * archived. Resolved by the pane alongside `alertCards`; a replayed summary
   * for one of these recaps past work rather than reporting news.
   */
  workedAlertIds?: ReadonlySet<string>;
  /**
   * Alerts the user opened while they were still unread, within the current
   * thread. Supplied by the pane, which is where every opener reports in — the
   * click that opens an alert also marks it read, so `alertCards` alone cannot
   * say whether the alert was new when it was opened. Absent, the recap
   * treatment falls back to the stored status, which is right for a reloaded
   * thread and wrong only in the window a live click creates.
   */
  freshlyOpenedAlertIds?: ReadonlySet<string>;
  /**
   * The signed-in user, so a move line can tell work handed to someone else
   * from the viewer's own reminder. Absent — a transcript rendered before the
   * session lands — draws no lines at all rather than guessing.
   */
  viewerUserId?: string | null;
  /**
   * The name the `'welcome'` row greets the viewer by — their non-blank
   * preferred name when set, else first name; resolved by the pane. Absent —
   * a transcript rendered before the session lands — the row renders only
   * its dated session divider and skips the greeting header, rather than
   * greeting nobody or leaving the sitting with no separator.
   */
  viewerGreetingName?: string | null;
  /**
   * Active-alert count the `'welcome'` row states, frozen by the pane when the
   * session opened. `null` renders the greeting alone (count not resolved, or
   * alert surfaces disabled) — the greeting never waits on it.
   */
  welcomeAlertCount?: number | null;
  /**
   * Rows the replies reference inline, resolved by the pane — same slot
   * discipline as `alertCards`. Absent renders every chip unresolved.
   */
  entities?: TranscriptEntities;
  /**
   * Send a suggested next action as an ordinary user turn — the pane wires it
   * to the same `sendMessage` the composer uses, so a click is indistinguishable
   * from the user typing those words. The second argument names the alert whose
   * expanded reply offered the suggestion (absent for chips on replies no alert
   * card claims), so the turn can be grounded in the row the chip was offered on.
   */
  onSuggestionSelect?: (text: string, sourceAlertId?: string) => void;
  /**
   * Open a row an inline reference names. Passed straight through rather than
   * wrapped like `onSuggestionSelect` — a chip click starts no turn, so there is
   * no scroll re-pin to do. Must be stable, or every bubble re-renders.
   */
  onEntitySelect?: (target: EntityTarget) => void;
}

/**
 * Pure presentational view for the assistant pane. Takes the
 * `useAssistantWebSocket` hook's return shape as props so Storybook stories
 * and visual-verification subagents can render every state from fixture
 * data without mocking the hook.
 *
 * Separators render above the bubble that opens them (see
 * {@link buildTranscriptDividers}): a date-stamped rule at each session gap, or
 * the lighter per-minute timestamp otherwise. Empty turns (no content, no
 * attachments) are skipped; tool-call detail is not rendered — a turn in flight
 * shows only a spinner bubble, and the details are surfaced to the console
 * behind the `debug-assistant` gate by {@link AssistantPane}.
 */
const AssistantPaneView = ({
  messages,
  isProcessing,
  isAbortable,
  isRehydrating,
  isAwaitingClientTool = false,
  connectionStatus,
  error,
  onSend,
  onStop,
  onReclaim,
  onClose,
  alertsSlot,
  alertsPresentation = 'truncated',
  alertsMotion,
  alertsReservedHeightPx = 0,
  composerNoticeSlot,
  isCoveredByAlerts = false,
  alertCards,
  workedAlertIds,
  freshlyOpenedAlertIds,
  viewerUserId,
  viewerGreetingName,
  welcomeAlertCount,
  entities,
  onSuggestionSelect,
  onEntitySelect,
}: IAssistantPaneViewProps) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const coveredRegionRef = React.useRef<HTMLDivElement>(null);
  const composerRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const transcriptColumnRef = React.useRef<HTMLDivElement>(null);
  const welcomeRowRef = React.useRef<HTMLDivElement>(null);
  const welcomeTailSpacerRef = React.useRef<HTMLDivElement>(null);

  useInert(coveredRegionRef, isCoveredByAlerts);
  useInert(composerRef, isCoveredByAlerts);

  const alertsFrame = useAlertsOverlayFrame({ rootRef, presentation: alertsPresentation, motion: alertsMotion });

  // Straight onto the DOM, not through state: the observer fires after layout
  // and a re-render would land the card one frame behind a growing textarea.
  const syncComposerHeight = React.useCallback(() => {
    const root = rootRef.current;
    const composer = composerRef.current;
    if (!root || !composer) {
      return;
    }
    root.style.setProperty(ASSISTANT_COMPOSER_HEIGHT_VAR, `${composer.offsetHeight}px`);
  }, []);
  React.useLayoutEffect(syncComposerHeight, [syncComposerHeight]);
  useResizeObserver({ ref: composerRef, onResize: syncComposerHeight });

  // Whether the user is pinned to the bottom. Updated on every scroll so the
  // auto-follow reflects the user's latest intent — scrolling up to read history
  // mid-stream parks this `false` and stops the follow until they return.
  const isPinnedRef = React.useRef(true);
  // One-shot: the next auto-follow should glide rather than jump. Set when the
  // user sends from a scrolled-up position so their bubble eases into view; the
  // effect consumes it on the first run so streaming stays an instant jump.
  const smoothFollowOnceRef = React.useRef(false);
  // While our own smooth send-follow animates, the browser fires a stream of
  // scroll events at intermediate (not-yet-bottom) positions. Those must not be
  // read as the user scrolling away, or `isPinnedRef` would flip false and a
  // delta arriving mid-glide would cancel the follow. Suppress reads until the
  // glide reaches the bottom; `suppressUntilMsRef` is the safety-cap deadline.
  const suppressScrollReadsRef = React.useRef(false);
  const suppressUntilMsRef = React.useRef(0);

  const handleScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const pinned = isPinnedToBottom(event.currentTarget);
    if (suppressScrollReadsRef.current) {
      // Lift suppression once the glide arrives (pinned) or the cap elapses;
      // until then ignore the animation's own intermediate scroll events.
      if (pinned || performance.now() >= suppressUntilMsRef.current) {
        suppressScrollReadsRef.current = false;
      } else {
        return;
      }
    }
    isPinnedRef.current = pinned;
  }, []);

  // Sending a message is an explicit "follow along" signal: re-pin so the
  // auto-follow effect carries the view down to the bubble the user just sent,
  // even if they had scrolled up to read history.
  const handleSend = React.useCallback(
    (content: string, attachments?: IObservationDocument[]) => {
      isPinnedRef.current = true;
      smoothFollowOnceRef.current = true;
      onSend(content, attachments);
    },
    [onSend]
  );

  // A suggestion click is a send — it routes through the same re-pin as typing
  // one, or the transcript would not follow to the turn it starts when the user
  // clicked from a scrolled-up position.
  const handleSuggestionSelect = React.useCallback(
    (text: string, sourceAlertId?: string) => {
      if (onSuggestionSelect === undefined) {
        return;
      }
      isPinnedRef.current = true;
      smoothFollowOnceRef.current = true;
      onSuggestionSelect(text, sourceAlertId);
    },
    [onSuggestionSelect]
  );

  // One pinned-aware auto-follow for both discrete new bubbles (length changes)
  // and token-level streaming, where a bubble's text grows in place with no
  // length change. Key off total content length, not the tail bubble's — the
  // streaming bubble is not always last (mid-turn queued user bubbles sit below
  // it), so a tail-only key would stall the follow. Streaming jumps instantly —
  // smooth scrolling would pile up animations at token rate — but a send sets
  // `smoothFollowOnceRef` so the user's own bubble glides into view once.
  const totalContentLength = messages.reduce((sum, m) => sum + m.content.length, 0);

  // A sitting opens with the welcome row at the TOP of the viewport, not the
  // history above it. The blank run after the last row (`welcomeTailSpacer`)
  // makes the bottom of the scroll range that position, so the auto-follow
  // below needs no second scroll target. Sized straight onto the DOM, like the
  // composer height above, and ahead of the follow effect: a layout effect
  // runs before the passive follow, so the jump to `scrollHeight` already sees
  // the spacer. Returns whether the spacer's height moved.
  const welcomeRowId = React.useMemo(() => messages.findLast((m) => m.kind === 'welcome')?.id ?? null, [messages]);
  const syncWelcomeTailSpacer = React.useCallback((): boolean => {
    const container = scrollContainerRef.current;
    const column = transcriptColumnRef.current;
    const spacer = welcomeTailSpacerRef.current;
    if (!container || !column || !spacer) {
      return false;
    }
    const welcomeRow = welcomeRowRef.current;
    // Measured without collapsing the spacer first: shrinking the scroll range
    // even for one layout clamps `scrollTop`, and that clamp fires a scroll
    // event that reads as the user leaving the bottom. The column's own height
    // less the spacer's, not `scrollHeight` — that never reads below the
    // viewport, which is exactly the case the spacer exists for.
    const height =
      welcomeRow === null
        ? 0
        : welcomeTailSpacerHeight({
            viewportHeight: container.clientHeight,
            contentHeight: column.offsetHeight - spacer.offsetHeight,
            welcomeOffsetTop:
              welcomeRow.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop,
          });
    const previousHeight = spacer.style.height;
    spacer.style.height = `${height}px`;
    return spacer.style.height !== previousHeight;
  }, []);
  React.useLayoutEffect(() => {
    syncWelcomeTailSpacer();
  }, [syncWelcomeTailSpacer, welcomeRowId, messages.length, totalContentLength]);

  // Heights that move without a message changing — the viewport (a window
  // resize, the Notification Center taking the composer's room) and rows that
  // fill in after they render (alert cards resolving). While the spacer is
  // still holding the welcome at the top, a pinned viewer stays there through
  // both; once it has collapsed, only a change to the spacer itself re-seats
  // them, so a smooth send-follow mid-glide is never cut short by a resize.
  const followWelcomeTailSpacer = React.useCallback(() => {
    const changed = syncWelcomeTailSpacer();
    const container = scrollContainerRef.current;
    const spacer = welcomeTailSpacerRef.current;
    if (!container || !spacer || !isPinnedRef.current) {
      return;
    }
    if (changed || spacer.offsetHeight > 0) {
      container.scrollTop = container.scrollHeight;
    }
  }, [syncWelcomeTailSpacer]);
  useResizeObserver({ ref: scrollContainerRef, onResize: followWelcomeTailSpacer });
  useResizeObserver({ ref: transcriptColumnRef, onResize: followWelcomeTailSpacer });

  React.useEffect(() => {
    if (!isPinnedRef.current) {
      return;
    }
    const container = scrollContainerRef.current;
    if (container) {
      if (smoothFollowOnceRef.current) {
        smoothFollowOnceRef.current = false;
        // Mask the animation's own scroll events until it settles at the bottom
        // (see `suppressScrollReadsRef`), so a delta landing mid-glide doesn't
        // read as the user scrolling away and cancel the follow.
        suppressScrollReadsRef.current = true;
        suppressUntilMsRef.current = performance.now() + SMOOTH_FOLLOW_SETTLE_TIMEOUT_MS;
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages.length, totalContentLength]);

  const isConnecting = connectionStatus === 'connecting';
  const isConnected = connectionStatus === 'connected';
  const isSuperseded = connectionStatus === 'superseded';
  // The superseded state has its own banner (with a re-claim action), so keep
  // it out of the generic error block even though its own `error` may be set.
  const hasError = !isSuperseded && (connectionStatus === 'error' || !!error);

  // While a round's text streams, its "working" affordance lives inline at the
  // bottom of the growing bubble (see `AssistantMessage`), so the standalone
  // spinner below is suppressed. Between rounds (tools running, or before the
  // first token) no bubble is streaming and the standalone spinner shows.
  const isStreaming = messages.some((m) => m.isStreaming === true);

  const dividers = React.useMemo(() => buildTranscriptDividers(messages), [messages]);

  // Ids of bubbles that continue a same-sender run (no divider) — the view tucks
  // them tight under the previous bubble so a burst from one side reads as a
  // group, not evenly-spaced singles.
  const groupedIds = React.useMemo(() => computeGroupedMessageIds(messages, dividers), [messages, dividers]);
  const spentSuggestionIds = React.useMemo(() => computeSpentSuggestionIds(messages), [messages]);

  // Which replies recap rather than report. Opening an alert always replays its
  // stored summary; on one the user has already read, is waiting on, or
  // archived, that summary describes a conversation that already happened, and
  // plain prose would have the assistant appear to say it again now. An alert
  // whose lookup has not landed reads as new, which is the common case and the
  // quieter thing to be wrong about.
  //
  // An alert opened from its own unread row stays excluded even once the stored
  // status says read, because it is that click that marked it — the treatment
  // asks whether the user had seen this before, not whether the row is read
  // now. The exclusion lasts as long as the thread it was earned in: across a
  // reload or a `/reset` the turn genuinely is a past conversation, and the
  // stored status is the right answer again.
  // Which alert each reply was opened on — read twice: the recap treatment
  // below, and each reply's `sourceAlertId` so a tapped suggestion's turn is
  // grounded in the alert that offered it.
  const alertIdByMessage = React.useMemo(() => alertIdByMessageId(messages), [messages]);

  const recapMessageIds = React.useMemo(() => {
    const ids = new Set<string>();

    for (const [messageId, alertId] of alertIdByMessage) {
      if (workedAlertIds?.has(alertId) === true && freshlyOpenedAlertIds?.has(alertId) !== true) {
        ids.add(messageId);
      }
    }

    return ids;
  }, [alertIdByMessage, workedAlertIds, freshlyOpenedAlertIds]);

  // Split the transcript so the thinking spinner sits between in-flight
  // messages (the current turn) and queued messages (the user typed them
  // mid-turn; they wait for the current turn's reply before being batched
  // into the next turn). Confirmed + in-flight rows go above the spinner;
  // queued rows go below.
  //
  // The split point is the first row that is pending AND not in flight —
  // i.e. the first queued bubble. Without this, B and C typed while A is
  // being processed would visually sit above the spinner, misleading the
  // user into thinking the assistant is already working on C when it is
  // still on A.
  const firstQueuedIndex = messages.findIndex((m) => m.isPending === true && m.isInFlight !== true);
  const messagesAboveThinking = firstQueuedIndex === -1 ? messages : messages.slice(0, firstQueuedIndex);
  const messagesBelowThinking = firstQueuedIndex === -1 ? [] : messages.slice(firstQueuedIndex);

  /**
   * The separator that opens a row, if it earned one. Shared by every
   * renderable row — a bubble and an alert card are both measured by
   * `buildTranscriptDividers`, so both have to draw what it hands back.
   */
  const renderDivider = (divider: TranscriptDivider | undefined) =>
    divider?.kind === 'session' ? (
      // Date-stamped rule opening a new sitting: a hairline on each side with
      // the label vertically centered on the line.
      <div className="flex items-center gap-3 py-2">
        <div className="border-secondary flex-1 border-t" />
        <span className="text-tertiary shrink-0 text-xs">{divider.label}</span>
        <div className="border-secondary flex-1 border-t" />
      </div>
    ) : divider?.kind === 'minute' ? (
      <div className="text-tertiary py-2 text-center text-xs">{divider.label}</div>
    ) : null;

  const renderMessageRow = (message: IConversationMessage) => {
    if (message.kind === 'tool-call') {
      return <ToolCallLine key={message.id} goal={message.content} />;
    }
    if (message.kind === 'welcome') {
      // The deterministic session opener. Outside the bubble treatment like an
      // alert card — it is the sitting's headline, not something anyone said —
      // and it always takes the dated session rule (`buildTranscriptDividers`
      // assigns welcome rows one unconditionally, and the row resets the gap
      // baseline, so the reply that follows draws no second rule). With no
      // greeting name only the header is skipped: the divider still renders,
      // so the sitting the row opened keeps its separator. The latest welcome
      // is the row the tail spacer seats at the top of the viewport.
      return (
        <div
          key={message.id}
          ref={message.id === welcomeRowId ? welcomeRowRef : undefined}
          className="flex flex-col gap-1"
        >
          {renderDivider(dividers.get(message.id))}
          {viewerGreetingName != null && (
            <AssistantWelcomeHeader
              greetingName={viewerGreetingName}
              activeAlertCount={welcomeAlertCount ?? null}
              isFirstSession={message.isFirstSession === true}
            />
          )}
        </div>
      );
    }
    if (message.kind === 'alert-card') {
      // Sits outside the bubble treatment for the same reason a tool-call line
      // does: it is the turn's subject, not something anyone said. It still
      // takes a divider, though — unlike a tool-call line, an alert card is a
      // renderable row, so `buildTranscriptDividers` measures the gap to it and
      // an alert opened after a long pause (or as the transcript's first row)
      // has earned the rule that opens a sitting.
      return (
        <div key={message.id} className="flex flex-col gap-1">
          {renderDivider(dividers.get(message.id))}
          <AlertCardHeader alert={alertCards?.[message.alertId ?? ''] ?? null} className="pt-2" />
        </div>
      );
    }
    if (!isRenderableMessage(message)) {
      return null;
    }
    const divider = dividers.get(message.id);
    return (
      <div
        key={message.id}
        // Tuck a same-sender continuation tight under the previous bubble (the
        // container's gap-4 is otherwise applied between every row). Only user
        // bubbles tuck — bubble-less assistant prose would weld into one wall of
        // text with no boundary to separate consecutive turns.
        className={cx('flex flex-col gap-1', groupedIds.has(message.id) && message.role === 'user' && '-mt-2.5')}
      >
        {renderDivider(divider)}
        <AssistantMessage
          message={message}
          entities={entities}
          onSuggestionSelect={handleSuggestionSelect}
          onEntitySelect={onEntitySelect}
          isRecap={recapMessageIds.has(message.id)}
          suggestionsSpent={spentSuggestionIds.has(message.id)}
          sourceAlertId={alertIdByMessage.get(message.id)}
        />
      </div>
    );
  };

  /**
   * The receipts under one tool-call report: what each of its calls left a
   * work item as, composed from facts frozen when the write landed. Rendered
   * inside the cluster so a report and its receipts read as one group, and so
   * the line never moves as the turn streams more prose.
   */
  const renderToolCallRow = (message: IConversationMessage): React.ReactNode[] => [
    renderMessageRow(message),
    ...(viewerUserId == null
      ? []
      : (message.workItemMoves ?? []).flatMap((fact) => {
          const line = workItemMoveLine(fact, viewerUserId);
          return line === null
            ? []
            : [<WorkItemMoveLine key={`move-${message.id}-${fact.workItemId}`} text={line.text} tone={line.tone} />];
        })),
  ];

  // Render a slice of the transcript. Consecutive tool-call lines render in a
  // single tight-gap cluster (gap-1) so they read as one activity group, while
  // the cluster keeps the normal gap-4 from its neighbors. The clustering (and
  // the dropping of empty rows that would otherwise split a cluster) is a pure,
  // unit-tested helper — see `clusterTranscriptRows`.
  const renderRows = (rows: IConversationMessage[]): React.ReactNode[] =>
    clusterTranscriptRows(rows).map((item) =>
      item.type === 'tool-cluster' ? (
        <div key={`tool-calls-${item.toolCalls[0].id}`} className="flex flex-col gap-1">
          {item.toolCalls.flatMap(renderToolCallRow)}
        </div>
      ) : (
        renderMessageRow(item.message)
      )
    );

  const inputPlaceholder = isSuperseded
    ? 'Open in another tab or window — choose "Reconnect" to continue'
    : isConnecting
      ? 'Connecting...'
      : isConnected
        ? "Ask about your family's finances..."
        : 'Connection paused...';

  return (
    <div
      ref={rootRef}
      style={{ [ASSISTANT_COMPOSER_GAP_VAR]: `${COMPOSER_CARD_GAP_PX}px` } as React.CSSProperties}
      className="relative flex h-full w-full flex-col"
    >
      {/* Everything the full Notification Center covers, in one region so it
          goes inert as one. */}
      <div ref={coveredRegionRef} className="flex min-h-0 flex-1 flex-col">
        {/* Header — no rule below it, per the Figma panel header (node
          8052:249487). `pt-2` (over the panel's own top inset) seats the title
          on the same line as the sidebar wordmark and the pane card's title.
          To start a fresh session, the user types `/reset`. */}
        <div className="flex items-center gap-3 px-4 pb-4 pt-2">
          <SparkleIcon aria-hidden="true" className="text-primary size-[18px] shrink-0" />
          <div className="text-primary text-xl font-semibold">{ASSISTANT_TITLE}</div>
          <ConnectionIndicator status={connectionStatus} />
          {onClose && (
            <div className="ml-auto">
              <CloseButton size="sm" label="Close assistant" onPress={onClose} />
            </div>
          )}
        </div>

        {/* Messages area — horizontal padding lives on the scroll container (not
          the inner column) so the max-w-3xl content box lands on the same left/
          right edge as the composer, whose wrapper carries the matching px-4. */}
        <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4">
          {/* pt-2, not py-6: with no rule under the header, the transcript sits
            close beneath it rather than opening on a band of empty ground. */}
          <div ref={transcriptColumnRef} className="mx-auto flex max-w-3xl flex-col gap-4 pb-6 pt-2">
            {/* Error message */}
            {hasError && (
              <div className="bg-error-primary text-error-primary rounded-lg p-4 text-sm">
                {error ?? 'Connection interrupted. Reconnecting while this tab is active.'}
              </div>
            )}

            {/* Loading spinner — covers both the cold-connect window (WS not
              yet open, often gated behind AuthProvider resolving session)
              AND the post-open rehydration handshake
              (`transcript_snapshot` + `assistant_ready`). Suppressed once
              local messages populate so a reconnect with stale messages
              keeps showing them until the fresh snapshot replaces. */}
            {(isConnecting || isRehydrating) && messages.length === 0 && !hasError && (
              <div className="flex flex-col items-center justify-center gap-3 py-16">
                <LoadingIndicator size="md" />
                <div className="text-tertiary text-sm">Loading your conversation…</div>
              </div>
            )}

            {/* Empty state */}
            {messages.length === 0 &&
              !isProcessing &&
              !hasError &&
              !isConnecting &&
              !isRehydrating &&
              !isSuperseded && <AssistantPaneEmptyState />}

            {/* Messages up to and including the message currently being
              processed (the oldest pending). */}
            {renderRows(messagesAboveThinking)}

            {/* A turn in flight with no text currently streaming (tools running,
              or before the first token): a bare spinner + animated "Thinking…"
              label on its own line — NOT in a bubble — sitting beneath any
              tool-activity lines (which are in `messagesAboveThinking`) and
              above any queued follow-ups. Suppressed while a round's text streams
              (that round's spinner lives inline in its growing bubble), and while
              a client tool awaits the browser (the user is mid-Plaid-flow — the
              tool-activity line already explains the wait). */}
            {isProcessing && !isStreaming && !isAwaitingClientTool && <ThinkingIndicator />}

            {/* User messages queued behind the current turn. Each is an
              optimistic bubble that hasn't yet been picked up by the server
              orchestrator's FIFO. */}
            {renderRows(messagesBelowThinking)}

            {/* Blank run that lets "scrolled to the bottom" seat the newest
              welcome at the top of the viewport; sized by
              `syncWelcomeTailSpacer`, zero once the sitting overflows. `-mt-4`
              cancels the column gap so a collapsed spacer adds no ground. */}
            <div ref={welcomeTailSpacerRef} aria-hidden="true" className="-mt-4 shrink-0" />
          </div>
        </div>

        {/* Superseded — the assistant is live in another tab/window. A thin
          bar pinned just above the composer (outside the scroll container, so it
          never scrolls away) offering an explicit re-claim. This tab never
          auto-reconnects — that would evict the other one in a loop. */}
        {isSuperseded && (
          <div className="border-secondary bg-warning-primary text-warning-primary flex items-center justify-center gap-1.5 border-t px-6 py-2 text-sm">
            <span>The assistant is open in another tab.</span>
            <button
              type="button"
              onClick={onReclaim}
              className="cursor-pointer font-semibold underline underline-offset-2 hover:no-underline"
            >
              Reconnect
            </button>
            <span>to continue (will disconnect other tab)</span>
          </div>
        )}
      </div>

      {/* Composer — sits directly on the ground with no rule; a short fade
          over the transcript's tail sets it off instead (Figma "Assistant
          Composer", the `gradient-bg` band). The spacer holds the card's
          place: the card itself is the overlay below, so growing it to the
          full center never reflows the transcript. */}
      <div className="relative">
        <div
          aria-hidden="true"
          className="from-bg-app pointer-events-none absolute inset-x-0 -top-5 h-5 bg-gradient-to-t to-transparent"
        />
        <div
          aria-hidden="true"
          style={{
            height: alertsReservedHeightPx > 0 ? alertsReservedHeightPx + COMPOSER_CARD_GAP_PX : 0,
            ...SIDE_PANEL_TRANSITION_DURATION_STYLE,
          }}
          className="transition-[height] ease-in-out motion-reduce:transition-none"
        />

        <div ref={composerRef} className="px-4 pb-4">
          {composerNoticeSlot}
          <div className="mx-auto max-w-3xl">
            {/* Input is always enabled. The hook queues sends when the WS is
                not OPEN + assistant_ready (drained on the next ready frame),
                and the server orchestrator FIFOs mid-turn user_messages on
                its own processing chain, so backpressure does not need a UI
                gate. The placeholder still reflects the underlying network
                state. */}
            <AssistantInput
              onSend={handleSend}
              isAbortable={isAbortable}
              onStop={onStop}
              placeholder={inputPlaceholder}
            />
          </div>
        </div>
      </div>

      {/* The Notification Center card. One element in one containing block for
          every presentation: anchored one gap above the input by the measured
          composer height, or covering the pane edge to edge when full. Capped
          at the transcript's and composer's `max-w-3xl` and centered, so the
          three read as one column on a wide pane.

          A presentation change tweens `height` and the anchor between pixel
          endpoints (`useAlertsOverlayFrame`); the transition classes apply only
          while one runs, so at rest the anchor still tracks a growing textarea
          instantly. The pin releases at settle: the full center goes back to
          `top-2 bottom-2` so a resize reflows it, a composer card back to its
          natural height, which equals the endpoint. */}
      {alertsSlot != null && (
        <div
          onTransitionEnd={alertsMotion?.onTransitionEnd}
          style={{
            ...(alertsFrame.heightPx !== null ? { height: alertsFrame.heightPx } : undefined),
            ...(alertsMotion ? SIDE_PANEL_TRANSITION_DURATION_STYLE : undefined),
          }}
          className={cx(
            'absolute z-10 mx-auto max-w-3xl',
            alertsMotion && 'transition-[height,bottom,left,right] ease-in-out motion-reduce:transition-none',
            alertsFrame.anchor === 'pane'
              ? cx('inset-x-3 bottom-2', alertsFrame.heightPx === null && 'top-2')
              : 'inset-x-4 bottom-[calc(var(--assistant-composer-h)+var(--assistant-composer-gap))]'
          )}
        >
          {alertsSlot}
        </div>
      )}
    </div>
  );
};

export default AssistantPaneView;
